/**
 * Bulk action bar - shown when cards are selected in selection mode.
 * Provides batch operations: edit, move, delete, add to deck/list.
 */
import type { ReactElement } from 'react';
import { GiCardPlay } from 'react-icons/gi';
import { IoCheckbox, IoClose, IoFolder, IoPencil, IoSquareOutline, IoTrash } from 'react-icons/io5';

import Button from '@/components/Button/Button';

export interface BulkActionBarProps {
  selectedCount: number;
  totalCount: number;
  onSelectAll: () => void;
  onEdit: () => void;
  onMoveToFolder: () => void;
  onAddToDeck: () => void;
  onDelete: () => void;
  onClearSelection: () => void;
  onClose: () => void;
}

export default function BulkActionBar({
  selectedCount,
  totalCount,
  onSelectAll,
  onEdit,
  onMoveToFolder,
  onAddToDeck,
  onDelete,
  onClearSelection,
  onClose,
}: BulkActionBarProps): ReactElement {
  const hasSelection = selectedCount > 0;
  const allSelected = selectedCount === totalCount && totalCount > 0;

  return (
    <div className="bg-aether/10 border-aether/30 mt-2.5 flex items-center gap-3 border-b px-6 py-2.5">
      <span className="text-pearl text-sm font-medium">{selectedCount.toLocaleString()} selected</span>

      <div className="bg-storm h-5 w-px" />

      {totalCount > 0 && (
        <button
          type="button"
          onClick={allSelected ? onClearSelection : onSelectAll}
          className="text-ash hover:text-pearl flex items-center gap-1.5 rounded-lg px-1.5 py-1.5 text-sm transition-colors"
        >
          {allSelected ? <IoCheckbox className="h-4 w-4" /> : <IoSquareOutline className="h-4 w-4" />}
          Select all
        </button>
      )}

      <div className="bg-storm h-5 w-px" />

      <Button icon={<IoPencil />} text="Edit" color="secondary" size="small" onClick={onEdit} disabled={!hasSelection} />
      <Button icon={<IoFolder />} text="Move" color="secondary" size="small" onClick={onMoveToFolder} disabled={!hasSelection} />
      <Button icon={<GiCardPlay />} text="Add to Deck" color="secondary" size="small" onClick={onAddToDeck} disabled={!hasSelection} />
      <Button icon={<IoTrash />} text="Delete" color="delete" variant="outlined" size="small" onClick={onDelete} disabled={!hasSelection} />

      <div className="ml-auto">
        <button
          type="button"
          onClick={onClose}
          className="text-ash hover:text-pearl rounded-md p-1.5 transition-colors"
          aria-label="Exit selection mode"
        >
          <IoClose className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
